import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Cache } from 'aws-amplify';
import { environment } from '../../../environments/environment';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfigService } from '../config';
import { ViewAsScopeService } from '../view-as-scope';
import { VoucherDemoModeService } from '../vouchers/demo/voucher-demo-mode.service';
import {
	applyVoucherDemoUserAttributes,
	VOUCHER_DEMO_VIEW_ROLE
} from '../vouchers/demo/voucher-demo.constants';
import { isImpersonationRuntimeEnabled } from './impersonation-runtime';

export interface ImpersonationToken {
	token: string;
	role: 'sadmin' | 'distiadmin' | 'marketadmin' | 'clientadmin' | 'campusadmin';
	distiId?: string | null;
	marketId?: string | null;
	clientId?: string | null;
	campusId?: string | null;
	portalView?: 'ops' | 'retail' | null;
	expiresAt: number; // Unix timestamp
}


const STORAGE_KEY = 'dev_impersonation_token';

@Injectable({
	providedIn: 'root'
})
export class DevImpersonationService {
	private tokenSubject = new BehaviorSubject<ImpersonationToken | null>(null);
	public token$: Observable<ImpersonationToken | null> = this.tokenSubject.asObservable();


	private impersonationEnabled = false;
	private devSwitcherEnabled = false;
	private cacheIntercepted = false;
	private originalCacheGetItem: typeof Cache.getItem;

	constructor(
		private permissionsService: NgxPermissionsService,
		private viewAsScopeService: ViewAsScopeService,
		private configService: ConfigService,
		private voucherDemoMode: VoucherDemoModeService
	) {
		this.devSwitcherEnabled = !environment.production;
		this.originalCacheGetItem = Cache.getItem.bind(Cache);

		if (this.configService.isLoaded()) {
			this.enableForRuntime(this.configService.get('host'));
		}

		this.token$.subscribe(token => {
			if (this.voucherDemoMode.isEnabled()) {
				this.applyDemoRetailPermissions();
				return;
			}
			if (token) {
				this.applyPermissions(token);
			} else {
				this.clearPermissions();
			}
		});
	}

	/**
	 * Turn impersonation on after config.json is loaded. Safe to call more than once.
	 * Production Angular builds only enable this when host is local or staging.
	 */
	enableForRuntime(host: string): void {
		this.devSwitcherEnabled = !environment.production;
		this.voucherDemoMode.enableForRuntime(host);
		if (!isImpersonationRuntimeEnabled(environment.production, host)) {
			this.impersonationEnabled = false;
			return;
		}

		this.impersonationEnabled = true;
		this.interceptCache();
		this.restorePersistedToken();
		if (this.voucherDemoMode.isEnabled()) {
			this.applyDemoRetailPermissions();
		}
	}

	/**
	 * Intercept Cache.getItem to apply impersonation overrides
	 * This ensures components reading directly from Cache get impersonated values
	 */
	private interceptCache(): void {
		if (this.cacheIntercepted) {
			return;
		}
		this.cacheIntercepted = true;

		Cache.getItem = (key: string): any => {
			const originalValue = this.originalCacheGetItem(key);

			if (key === 'userAttr' && originalValue) {
				return this.getUserAttributes();
			}

			return originalValue;
		};
	}

	/**
	 * Full local Dev Mode switcher. Never enabled in production Angular builds.
	 */
	isDevMode(): boolean {
		return this.devSwitcherEnabled;
	}

	/**
	 * Super-admin impersonation for local and staging hosts. Never enabled when
	 * config.json host is prod.
	 */
	isImpersonationEnabled(): boolean {
		return this.impersonationEnabled;
	}

	/**
	 * Get current impersonation token
	 */
	getToken(): ImpersonationToken | null {
		const token = this.tokenSubject.value;
		if (token && this.isTokenExpired(token)) {
			this.clearToken();
			return null;
		}
		return token;
	}

	/**
	 * Set impersonation token
	 */
	setToken(tokenData: ImpersonationToken): void {
		if (!this.impersonationEnabled) {
			return;
		}

		this.tokenSubject.next(tokenData);
		this.saveToStorage(tokenData);
	}

	/**
	 * Clear impersonation token
	 */
	clearToken(): void {
		if (!this.impersonationEnabled) {
			return;
		}

		this.tokenSubject.next(null);
		this.clearStorage();
	}

	/**
	 * Check if token is expired
	 */
	isTokenExpired(token: ImpersonationToken): boolean {
		return Date.now() >= token.expiresAt * 1000;
	}

	/**
	 * Get impersonation token for headers (returns null if expired)
	 */
	getTokenForHeader(): string | null {
		const token = this.getToken();
		return token ? token.token : null;
	}

	/**
	 * Read the stored JWT for Amplify custom headers. The prod Amplify endpoint
	 * never calls this; local and staging do, including production Angular builds
	 * on the staging portal.
	 */
	static getTokenFromStorage(): string | null {
		try {
			const stored = sessionStorage.getItem(STORAGE_KEY);
			if (!stored) {
				return null;
			}
			const tokenData = JSON.parse(stored);
			if (tokenData.expiresAt && Date.now() >= tokenData.expiresAt * 1000) {
				sessionStorage.removeItem(STORAGE_KEY);
				return null;
			}
			return tokenData.token || null;
		} catch (e) {
			return null;
		}
	}

	/**
	 * Save token to sessionStorage
	 */
	private saveToStorage(token: ImpersonationToken): void {
		try {
			sessionStorage.setItem(STORAGE_KEY, JSON.stringify(token));
		} catch (e) {
			// Silently fail - sessionStorage may not be available
		}
	}

	/**
	 * Load token from sessionStorage
	 */
	private loadFromStorage(): ImpersonationToken | null {
		try {
			const stored = sessionStorage.getItem(STORAGE_KEY);
			return stored ? JSON.parse(stored) : null;
		} catch (e) {
			return null;
		}
	}

	private restorePersistedToken(): void {
		if (this.tokenSubject.value) {
			return;
		}

		const saved = this.loadFromStorage();
		if (saved && !this.isTokenExpired(saved) && saved.token) {
			this.tokenSubject.next(saved);
			return;
		}
		if (saved) {
			this.clearStorage();
		}
	}

	/**
	 * Clear token from sessionStorage
	 */
	private clearStorage(): void {
		try {
			sessionStorage.removeItem(STORAGE_KEY);
		} catch (e) {
			// Silently fail - sessionStorage may not be available
		}
	}


	/**
	 * Get user attributes with override applied (backward compatibility)
	 */
	getUserAttributes(): any {
		if (this.voucherDemoMode.isEnabled()) {
			const originalAttr = this.viewAsScopeService.getOriginalUserAttributes()
				|| this.originalCacheGetItem('userAttr');
			if (!originalAttr) {
				return originalAttr;
			}
			return applyVoucherDemoUserAttributes(originalAttr as Record<string, unknown>);
		}

		// View As scope takes precedence (works in production)
		if (this.viewAsScopeService.isEnabled()) {
			return this.viewAsScopeService.getEffectiveUserAttributes();
		}

		// Get original user attributes
		const userAttr = this.originalCacheGetItem('userAttr');

		if (!this.impersonationEnabled || !userAttr) {
			return userAttr;
		}

		// Get override from impersonation token
		const token = this.getToken();
		if (!token) {
			return userAttr;
		}

		// Create a copy with overridden values
		const overriddenAttr = { ...userAttr };
		overriddenAttr['custom:role'] = token.role;
		if (token.role === 'campusadmin' && token.portalView === 'retail') {
			overriddenAttr['custom:portal_view'] = 'retail';
		} else {
			overriddenAttr['custom:portal_view'] = 'ops';
		}

		// Override IDs based on impersonation token
		if (token.distiId !== null && token.distiId !== undefined) {
			overriddenAttr['custom:disti_id'] = token.distiId;
		} else {
			overriddenAttr['custom:disti_id'] = null;
		}

		if (token.marketId !== null && token.marketId !== undefined) {
			overriddenAttr['custom:market_id'] = token.marketId;
		} else {
			overriddenAttr['custom:market_id'] = null;
		}

		if (token.clientId !== null && token.clientId !== undefined) {
			overriddenAttr['custom:client_id'] = token.clientId;
		} else {
			overriddenAttr['custom:client_id'] = null;
		}

		if (token.campusId !== null && token.campusId !== undefined) {
			overriddenAttr['custom:campus_id'] = token.campusId;
		} else {
			overriddenAttr['custom:campus_id'] = null;
		}

		return overriddenAttr;
	}

	/**
	 * Get role with override applied (backward compatibility)
	 */
	getRole(): string | null {
		if (this.voucherDemoMode.isEnabled()) {
			return VOUCHER_DEMO_VIEW_ROLE;
		}

		// View As scope takes precedence (works in production)
		if (this.viewAsScopeService.isEnabled()) {
			const scope = this.viewAsScopeService.getScope();
			return scope && scope.role != null ? scope.role : null;
		}

		if (this.impersonationEnabled) {
			const token = this.getToken();
			if (token) {
				return token.role;
			}
		}

		// Fall back to original user role
		const userAttr = this.originalCacheGetItem('userAttr');
		return userAttr ? userAttr['custom:role'] : null;
	}

	/**
	 * Get original user attributes (without override applied)
	 * Useful for checking actual user permissions
	 */
	getOriginalUserAttributes(): any {
		return this.originalCacheGetItem('userAttr');
	}

	/**
	 * Get original user role (without override applied)
	 * Uses ViewAsScopeService which captured the real Cache before any patches,
	 * so this always returns the actual role even when View As is enabled.
	 */
	getOriginalRole(): string | null {
		const userAttr = this.viewAsScopeService.getOriginalUserAttributes();
		return userAttr ? userAttr['custom:role'] : null;
	}

	private applyDemoRetailPermissions(): void {
		this.permissionsService.flushPermissions();
		this.permissionsService.addPermission(VOUCHER_DEMO_VIEW_ROLE);
	}

	/**
	 * Apply permissions based on impersonation token
	 */
	private applyPermissions(token: ImpersonationToken): void {
		if (token.role) {
			this.permissionsService.flushPermissions();
			this.permissionsService.addPermission(token.role);
		}
	}

	/**
	 * Clear permissions and restore original
	 */
	private clearPermissions(): void {
		const userAttr = this.originalCacheGetItem('userAttr');
		if (userAttr && userAttr['custom:role']) {
			this.permissionsService.flushPermissions();
			this.permissionsService.addPermission(userAttr['custom:role']);
		}
	}
}
